import { PAGINGATION_DEFAULT } from '@const/pagination'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchList,
  fetchSingle,
  fetchDisplayScreenOptions,
  fetchListArea,
  fetchSingleArea,
  fetchFilterAndSortOptions } from './asyncAction'

const initialState: TPageSetupManagement.TState = {
  pageSetupList: {
    data: [],
    ...PAGINGATION_DEFAULT
  },
  searchParams: {
    title: '',
    screenTypeCode: '',
    isPublished: null
  },
  pageSetupUpsert: {
    data: {
      id: '',
      title: '',
      intro: '',
      displayOrder: 0,
      displayScreenTypeCode: '',
      showTitle: false,
      directTo: 'InApp',
      targetUrl: '',
      displayScreenOptionId: '',
      pageSetupAreas: [],
      published: false,
      selectedFilteredByOptions: [[], []],
      selectedSortedByOptions: [],
      image: {},
      imageUrl: ''
    }
  },
  pageSetupAreaList: {
    data: []
  },
  pageSetupAreaUpsert: {
    data: {
      id: '',
      name: ''
    },
    errors: [
      { key: 'name', message: '' }
    ]
  },
  sortAndFilterOptionList: {
    data: {
      sortedByOptions: [],
      filteredByOptions: []
    }
  },
  displayScreenOptionList: {
    data: []
  },
  isLoading: false,
  isFetchingList: false
}
const pageSetupManagementSlice = createSlice({
  name: 'pageSetupManagement',
  initialState,
  reducers: {
    changePageSetupAreaName (state, action) {
      state.pageSetupAreaUpsert.data.name = action.payload
    },
    setIsLoading (state, action) {
      state.isLoading = action.payload
    },
    resetFormValue () {
      return initialState
    },
    resetPageSetupUpsertValue: state => {
      state.pageSetupUpsert = initialState.pageSetupUpsert
    },
    setSelectedAreas: (state, action) => {
      const { checked, id } = action.payload
      const area = state.pageSetupAreaList.data.filter(x => x.id === id)[0]
      if (area) {
        area.checked = checked
      }
    },
    setSelectedAreaToDeleteId: (state, action) => {
      state.pageSetupAreaUpsert.data.id = action.payload
    },
    resetUpdateArea: state => {
      state.pageSetupAreaUpsert.data = initialState.pageSetupAreaUpsert.data
    },
    setUpdatedAreaName: (state, action) => {
      state.pageSetupAreaUpsert.data.name = action.payload
    },
    setUpdatedAreaError: (state, action) => {
      const error = state.pageSetupAreaUpsert.errors.filter(x => x.key === action.payload.key)[0]
      if (error) {
        error.message = action.payload.message
      }
    },
    removeSelectedFilteredOption: (state, action) => {
      const { selectedFilteredByOptions } = state.pageSetupUpsert.data
      if (selectedFilteredByOptions) {
        const updatedGroup = selectedFilteredByOptions[action.payload.group]
        if (updatedGroup) {
          updatedGroup.splice(action.payload.index, 1)
        }
      }
    },
    addSelectedFilteredOption: (state, action) => {
      const { selectedFilteredByOptions } = state.pageSetupUpsert.data
      if (selectedFilteredByOptions) {
        const updatedGroup = selectedFilteredByOptions[action.payload.group]
        if (updatedGroup) {
          updatedGroup.push({
            fieldName: '',
            value: ''
          })
        }
      }
    },
    changeFilteredOptionFieldName: (state, action) => {
      const { selectedFilteredByOptions } = state.pageSetupUpsert.data
      if (selectedFilteredByOptions) {
        const updatedGroup = selectedFilteredByOptions[action.payload.group]
        if (updatedGroup) {
          const option = updatedGroup[action.payload.index]
          if (option) {
            option.fieldName = action.payload.fieldName
          }
        }
      }
    },
    changeFilteredOptionValue: (state, action) => {
      const { selectedFilteredByOptions } = state.pageSetupUpsert.data
      if (selectedFilteredByOptions) {
        const updatedGroup = selectedFilteredByOptions[action.payload.group]
        if (updatedGroup) {
          const option = updatedGroup[action.payload.index]
          if (option) {
            option.value = action.payload.value
          }
        }
      }
    },
    changeSortedOption: (state, action) => {
      const { selectedSortedByOptions } = state.pageSetupUpsert.data
      if (selectedSortedByOptions) {
        selectedSortedByOptions[action.payload.index] = action.payload.value
      }
    },
    initSelectedSortedOptionList: (state, action) => {
      state.pageSetupUpsert.data.selectedSortedByOptions = action.payload
    },
    changeSearchTitle: (state, action) => {
      state.searchParams.title = action.payload
    },
    changeSearchDisplayScreenType: (state, action) => {
      state.searchParams.screenTypeCode = action.payload
    },
    changeSearchPublish: (state, action) => {
      state.searchParams.isPublished = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(fetchList.fulfilled, (state, action: PayloadAction<TPageSetupManagement.TState['pageSetupList']>) => {
        if (!action.payload?.data) return
        state.pageSetupList = {
          ...action.payload
        }
        state.isFetchingList = false
      })
      .addCase(fetchList.pending, state => {
        state.isFetchingList = true
      })
      .addCase(fetchSingle.fulfilled, (state, action: PayloadAction<TPageSetupManagement.TState['pageSetupUpsert']>) => {
        state.pageSetupUpsert.data = action.payload.data
        state.pageSetupAreaList.data.filter(x => action.payload.data.pageSetupAreas
          .includes(x.id)).map(a => {
          a.checked = true
          return a
        })
        state.isLoading = false
      })
      .addCase(fetchSingle.pending, state => {
        state.isLoading = true
      })
      .addCase(fetchListArea.fulfilled, (state, action: PayloadAction<TPageSetupManagement.TState['pageSetupAreaList']>) => {
        state.pageSetupAreaList.data = action.payload.data
        state.pageSetupAreaList.data.filter(x => state.pageSetupUpsert.data.pageSetupAreas
          .includes(x.id)).map(a => {
          a.checked = true
          return a
        })
      })
      .addCase(fetchSingleArea.fulfilled, (state, action: PayloadAction<TPageSetupManagement.TState['pageSetupAreaUpsert']>) => {
        state.pageSetupAreaUpsert.data = action.payload.data
      })
      .addCase(fetchDisplayScreenOptions.fulfilled, (state, action: PayloadAction<TPageSetupManagement.TState['displayScreenOptionList']>) => {
        state.displayScreenOptionList.data = action.payload.data
      })
      .addCase(fetchFilterAndSortOptions.fulfilled, (state, action: PayloadAction<TPageSetupManagement.TState['sortAndFilterOptionList']>) => {
        state.sortAndFilterOptionList.data = action.payload.data
      })
  }
})

export * from './asyncAction'

export const {
  setIsLoading,
  resetFormValue,
  setSelectedAreas,
  setSelectedAreaToDeleteId,
  resetUpdateArea,
  setUpdatedAreaName,
  setUpdatedAreaError,
  resetPageSetupUpsertValue,
  removeSelectedFilteredOption,
  addSelectedFilteredOption,
  changeFilteredOptionFieldName,
  changeFilteredOptionValue,
  changeSortedOption,
  initSelectedSortedOptionList,
  changeSearchTitle,
  changeSearchDisplayScreenType,
  changeSearchPublish
} = pageSetupManagementSlice.actions

export const pageSetupManagementReducer = pageSetupManagementSlice.reducer
